import { toast } from 'react-toastify'

import { handleError } from './apiUtils'
import { getConfigurationAsync, xKMSClient } from './httpMethods'
import {
  DeleteKeyRequestDto,
  EnrollmentKeyEntityRequestDto,
  GenericKeyResponseDto,
  GetKeysRequestDto,
  KeyEntityResponseDto,
  KeysResponseDto,
  LocalAccessKeysApi,
} from './kmsapiclient'

/**
 * Adds a local access key asynchronously.
 *
 * @param file - The file path of the local access key to upload.
 * @param keyName - The name of the local access key.
 * @param aadRevocation - The AAD revocation status.
 * @returns A promise that resolves to a `GenericKeyResponseDto` object.
 */
export async function addLocalAccessKeyAsync(
  file: string,
  keyName: string,
  aadRevocation: string,
): Promise<GenericKeyResponseDto> {
  try {
    const localAccessKeysApi = new LocalAccessKeysApi(getConfigurationAsync())
    const response = (
      await localAccessKeysApi.enrollmentKeyUpload(
        file,
        keyName,
        aadRevocation,
        xKMSClient,
      )
    ).data
    return response
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}

/**
 * Searches for local access keys based on the provided request.
 *
 * @param request - The request object containing search criteria.
 * @returns A promise that resolves to the search response.
 */
export async function getLocalAccessKeysAsync(
  keysRequest: GetKeysRequestDto,
): Promise<KeysResponseDto> {
  try {
    const localAccessKeysApi = new LocalAccessKeysApi(getConfigurationAsync())
    const response = (
      await localAccessKeysApi.enrollmentKeyGet(xKMSClient, keysRequest)
    ).data
    return response
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}

/**
 * Retrieves a local access key based on the provided request.
 *
 * @param keyId - The ID of the local access key to retrieve.
 * @returns A promise that resolves to the local access key response.
 */
export async function saveLocalAccessKeysAsync(
  key: EnrollmentKeyEntityRequestDto,
): Promise<KeyEntityResponseDto> {
  const handleError = (res) => {
    let errorMessage = res.message
    const errorCode =
      res.response && res.response.data && res.response.data.errorCode

    if (errorCode) {
      if (
        errorCode == 420 &&
        confirm(
          'This key has been modified by another session.\n\nReload current session?\n\nThe most recent changes will be discarded.',
        )
      ) {
        location.reload()
        return
      } else {
        errorMessage = `Please contact your administrator. (Error Code: ${errorCode}, Status Code: ${res.response.status})`
      }
    }
    toast.error(errorMessage)
  }

  try {
    const localAccessKeysApi = new LocalAccessKeysApi(getConfigurationAsync())
    const response = (
      await localAccessKeysApi.enrollmentKeyUpdate(xKMSClient, key)
    ).data
    return response
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}

/**
 * Deletes a local access key based on the provided request.
 *
 * @param key - The key to delete.
 * @returns A promise that resolves to the status of the removal.
 */
export async function deleteLocalAccessKeysAsync(key: any): Promise<string> {
  const request: DeleteKeyRequestDto = {
    keyId: key.keyId,
  }
  try {
    const localAccessKeysApi = new LocalAccessKeysApi(getConfigurationAsync())
    const response = (
      await localAccessKeysApi.enrollmentKeyDelete(xKMSClient, request)
    ).data
    return response
  } catch (error) {
    handleError(error)
    return Promise.reject(error)
  }
}
